import numeral from 'numeral';
import moment from 'moment';
import { MEASUREMENT_UNIT_MAX_DECIMALS, SETTINGS_TYPE_NAMES } from '../constants';

// -------------------------------------------
// Date utilities
// -------------------------------------------

const dateFormats = {
	DAY_MONTHWORD_YEAR: 'DD MMM YYYY',
	serviceRunPlanned: 'DD MMM YYYY',
	DAY_MONTHWORD_YEAR_HOUR_MINUTE_AMPM: 'DD MMM YYYY hh:mm a',
	DAY_MONTHWORD_FULL_YEAR_HOUR_MINUTE_AMPM: 'D MMMM YYYY h:mma',
	DAY_MONTHWORD_YEAR_HOUR_MINUTE_SECOND_AMPM: 'DD MMM YYYY hh:mm:ss a',
	HOUR_MINUTE_AMPM: 'hh:mm a',
};

export function formatDate (date, format) {
	// Format dates from the possible formats in vending-settings.js
	const formattedDate = moment(date).format(dateFormats[format]);

	return formattedDate;
}

// This function takes a raw date (in local time) and a GMT offset
// to correct the moment date object. This is useful when localDateTime
// is in local time but is assumed to be UTC when it hits the server!
export function adjustDateWithGMTOffset ({ localDateTime, GMTOffset, returnLocalTime }) {
	// If the localDateTime is moment-ified it will assume UTC, therefore
	// need to reconstruct it and set the correct GMT offset

	// TODO: remove this duplication and conditionally return this with .utc() by default
	if (returnLocalTime) {
		// Since the date is in local time (with a timezone offset)
		// we need to reconstruct it by assuming it is utc time
		// then manually setting the GMTOffset to get the final
		// moment date object (in local time with timezone offset).
		// This will then get set the correct UTC time when it hits mongo!
		const localTime = moment([
			moment(localDateTime).year(),
			moment(localDateTime).month(),
			moment(localDateTime).date(),
			moment(localDateTime).hours(),
			moment(localDateTime).minutes(),
			moment(localDateTime).seconds(),
		])
			// Manually set the GMT offset
			.utcOffset(
				Number(GMTOffset),
				// This locks in the local time and only sets the GMT Offset
				true,
			);

		return localTime;
	}

	// Since the date is in local time (with a timezone offset)
	// we need to reconstruct it by assuming it is utc time
	// then manually setting the GMTOffset to get the final
	// moment date object (in local time with timezone offset).
	// This will then get set the correct UTC time when it hits mongo!
	const adjustedUTCDate = moment([
		moment(localDateTime).year(),
		moment(localDateTime).month(),
		moment(localDateTime).date(),
		moment(localDateTime).hours(),
		moment(localDateTime).minutes(),
		moment(localDateTime).seconds(),
	])
		// Manually set the GMT offset
		.utcOffset(
			Number(GMTOffset),
			// This locks in the local time and only sets the GMT Offset
			true,
		)
		// Convert this to UTC before returning!
		.utc();

	return adjustedUTCDate;
}



// -------------------------------------------
// Number and currency utilities
// -------------------------------------------

export function formatIntAsPercentage (percentage) {
	// Used to convert ints to a percentage, string.

	if (percentage === null || percentage === undefined) {
		return 'n/a';
	}

	return numeral(percentage / 100).format('0%');
}

export function formatPercentage (percentage) {
	// Used to convert floats to a percentage, string.

	// Don't use !percentage, so we can show 0%.
	if (percentage === undefined || percentage === null || percentage === 'n/a') {
		return 'n/a';
	}

	return numeral(percentage).format('0%');
}


export function formatCurrency (integer, ignoreDollarSign = false) {
	// Used to convert integers to dollars.

	if ((integer === null) || (integer === undefined)) {
		return null;
	}
	if (integer) {
		return numeral(integer).divide(100).format(`${ignoreDollarSign ? '' : '$'}0,0.00`);
	}

	return (`${ignoreDollarSign ? '' : '$'}0`);
}

export const centsToDollars = value => Math.round(value || 0) / 100;

export function formatNumber (integer) {
	// Used to convert integers to have commas to separate thousands.
	// Checks if value exists first.

	if ((integer === null) || (integer === undefined)) {
		return null;
	}
	if (integer) {
		return numeral(integer).format('0,0');
	}

	return ('0');
}


export function roundNumber (number) {
	return number ? Number(number.toFixed(2)) : 0;
}

export function formatTemperature (temperature) {
	// Seems generally correct and safe
	if ((temperature === null) || (temperature === undefined)) {
		return null;
	}

	// We store temperatures as ints with two decimals
	// Better not to use numeral library, because dex-reported temperature vary in terms of the number of
	// significant figures they go to (Some go to one decimal, some to none).
	// Therefore, by just using parseFloat, we get the relevant number of decimals.
	const dividedTemperature = parseFloat(temperature / 100, 10);

	// Currently formatting with "C", can add more code to handle "F" later
	return `${dividedTemperature}° C`;
}

export function formatParlevelAndQuantity (value, product) {
	// Used to show value of planogram item fields
	// due to unusual checking of falsiness of value when 0

	if ((value !== undefined) && (value !== null)) {
		const decimals = getParlevelQuantityDecimals(product);

		return Number(parseFloat(value / Math.pow(10, decimals)).toFixed(decimals));
	}

	return '-';
}


// -------------------------------------------
// Date utilities
// -------------------------------------------

export function isEarlierThanToday (date) {
	// Returns true if the date is yesterday or earlier

	if (moment(date).diff(moment().startOf('day')) < 0) {
		return true;
	}

	return false;
}

export function allSelected (itemsToCheck, stateObj) {
	/*

	Accepts an array of IDs and a state object
	of IDs that are set to true or false.

	Returns true if all IDs in the array are
	in the state object as true, otherwise,
	returns false.

	Used in checking if all checkbox IDs are set
	to true in state.

	*/

	if (!stateObj) {
		// Fallback in case stateObject is incorrectly passed in.
		// Should not trigger; is a pure fallback.
		console.log('You did not pass a valid state object to allSelected()');
		return null;
	}

	// checks if items passes to check are objects
	const areObjects = itemsToCheck && itemsToCheck[0] && itemsToCheck[0].id;

	const allSelected = itemsToCheck.every(item => {
		return stateObj[areObjects ? item.id : item];
	});

	return allSelected;
}

export function generateUniqueId (existingIds) {
	let uniqueId = `id-${Math.random().toString(36).substr(2, 16)}`;
	if (existingIds[uniqueId]) {
		uniqueId = generateUniqueId(existingIds);
	}
	return uniqueId;
}

// Simple sort

export function simpleSort (fieldName, sortOrder) {
	// A new (currently uncomprehensive) sort function for simple sorting
	// by a field, ascending and descending.

	if (sortOrder === 'asc') {
		return (a, b) => {
			if (a[fieldName] < b[fieldName]) {
				return -1;
			}
			if (a[fieldName] > b[fieldName]) {
				return 1;
			}
			return 0;
		};
	}
	if (sortOrder === 'dsc') {
		return (a, b) => {
			if (a[fieldName] < b[fieldName]) {
				return 1;
			}
			if (a[fieldName] > b[fieldName]) {
				return -1;
			}
			return 0;
		};
	}
}

/*
 * This function is used to sort list of any items: objects, integers, strings.
 */
export const compareItems = (sortBy, sortDirection, sortByResolver) => (a, b) => {
	let subresult = 0;

	// assign field values to variables (use the resolver method if provided)
	const valueA = sortByResolver ? sortByResolver(a, sortDirection) : a[sortBy];
	const valueB = sortByResolver ? sortByResolver(b, sortDirection) : b[sortBy];

	const fieldType = typeof (valueA === undefined || valueA === null ? valueB : valueA);

	// Handle comparison between zero and null in a specific way
	// I think it's okay to do it like this? If we wanted to do this within the
	// number and object comparison, seems like that would be messier...
	// Maybe it would be improved in general if null values went to the end of the list though.
	if (valueA === null && valueB === 0) {
		return 1;
	}
	if (valueA === 0 && valueB === null) {
		return -1;
	}
	// sort numbers
	if (fieldType === 'number') {
		subresult = valueA - valueB;
	}
	// sort strings. note we keep dates as strings so have to check if it's a date or not.
	else if (fieldType === 'string') {
		if (valueA && valueB) {
			subresult = valueA.localeCompare(valueB);
		}
		else {
			subresult = valueB ? -1 : 1;
		}
	}
	// sort objects. note: null is also an object.
	else if (fieldType === 'object') {
		// field names to check (currently it's only 'name' i.e. location.name etc). add more if required.
		const fieldNames = ['name'];

		// for each field name
		for (const fieldName of fieldNames) {
			// if valueA has field and B hasn't
			if (valueA && valueA[fieldName] && (!valueB || !valueB[fieldName])) {
				subresult = 1;
				break;
			}
			// if valueB has field and A hasn't
			else if (valueB && valueB[fieldName] && (!valueA || !valueA[fieldName])) {
				subresult = -1;
				break;
			}
			// perform STRING comparison if both objects have field
			else if (valueB && valueB[fieldName] && valueA && valueA[fieldName]) {
				subresult = valueA[fieldName].localeCompare(valueB[fieldName]);
				break;
			}
		}
	}
	// Sort Booleans
	else if (fieldType === 'boolean') {
		if (valueA === valueB) {
			subresult = 0;
		}
		if (valueA && !valueB) {
			subresult = -1;
		}
		if (valueB && !valueA) {
			subresult = 1;
		}
	}
	return sortDirection * subresult;
};

// semi shallow now
export const areEqualShallow = (a, b) => {
	// array comparison
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) {
			return false;
		}

		// check if some value changed
		return a.every((value, index) => {
			// Compare dates
			if (value instanceof Date) {
				if (value - b[index] !== 0) {
					return false;
				}
			}
			// eslint-disable-next-line eqeqeq
			else if (value != b[index]) { // not comparing type until we stop sending some int values as strings
				return false;
			}

			return true;
		});
	}

	for (const key in a) {
		// For JS date objects, check if difference between them is not zero - checking
		// equivalence the normal way doesn't work, I think
		if (a[key] instanceof Date) {
			if (a[key] - b[key] !== 0) {
				return false;
			}
		}
		else if (Array.isArray(a[key]) && Array.isArray(b[key])) {
			const areEqual = areEqualShallow(a[key], b[key]);

			if (!areEqual) {
				return false;
			}
		}
		// eslint-disable-next-line eqeqeq
		else if (a[key] != b[key]) { // TODO not comparing type until we stop sending some int values as strings
			return false;
		}
	}
	return true;
};

export function getRandomString (length) {
	const radom13chars = function () {
		return Math.random().toString(16).substring(2, 15);
	};

	const loops = Math.ceil(length / 13);

	return new Array(loops).fill(radom13chars).reduce((string, func) => {
		return string + func();
	}, '').substring(0, length);
}

export function getRandomArrayElement (array) {
	return array[Math.floor(Math.random() * array.length)];
}

export function capitalizeFirstLetter (string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getParlevelQuantityUnit (product) {
	return Math.pow(10, getParlevelQuantityDecimals(product));
}

export function getParlevelQuantityDecimals (product) {
	const measurementUnit = product?.measurementUnit || 0;

	return MEASUREMENT_UNIT_MAX_DECIMALS[measurementUnit];
}

export function truncateText (text, truncateTo) {
	if (!text || !truncateTo || text.length < truncateTo) {
		return text;
	}
	return `${text.substring(0, truncateTo)}...`;
}

export const insertIntoArray = (arr, index, newItem) => [
	// part of the array before the specified index
	...arr.slice(0, index),
	// inserted item
	newItem,
	// part of the array after the specified index
	...arr.slice(index),
];

export function escapeHtml (text) {
	const map = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		'\'': '&#039;',
	};

	return text.replace(/[&<>"']/g, m => map[m]);
}

export function isObject (object) {
	return object instanceof Object && !(object instanceof Array);
}

export function camelize (str) {
	return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
		if (+match === 0) {
			return ''; // or if (/\s+/.test(match)) for white spaces
		}
		return index === 0 ? match.toLowerCase() : match.toUpperCase();
	});
}

export function valueExists (value) {
	return value !== null && value !== undefined;
}

export function isFunction (value) {
	return value && (value instanceof Function);
}

export const getSettingsGroupName = settingsType => `${SETTINGS_TYPE_NAMES[settingsType]?.toLowerCase()}s`;

export const getDiscountPriceLabel = (price, discountPrice) => {
	let value = valueExists(discountPrice) ? formatCurrency(discountPrice) : formatCurrency(price);

	if (valueExists(discountPrice) && discountPrice !== price) {
		value += ` (${formatCurrency(price)})`;
	}

	return value;
};

export const decode64Value = (value) => {
	return Buffer.from(value, 'base64').toString();
};

export const code64Value = (value) => {
	return Buffer.from(value).toString('base64');
};

export const deepEqual = (obj1, obj2) => {

	if (obj1 === obj2) {
		return true; // it's just the same object. No need to compare.
	}

	// compare primitives
	if (isPrimitive(obj1) && isPrimitive(obj2)) {
		return obj1 === obj2;
	}

	// compare objects with same number of keys

	// eslint-disable-next-line guard-for-in
	for (const key in obj1) {
		if (!(key in obj2)) {
			return false;
		}
		if (!deepEqual(obj1[key], obj2[key])) {
			return false;
		}
	}

	return true;
};

//	check if value is primitive
const isPrimitive = (obj) => {
	return (typeof obj !== 'object' || !Array.isArray(obj));
};


export function difference (arrA, arrB) {
	const setA = new Set(arrA);
	const setB = new Set(arrB);
	const difference = new Set(setA);
	for (const elem of setB) {
		difference.delete(elem);
	}

	return [...difference];
}

export function intersection (arrA, arrB) {
	const setA = new Set(arrA);
	const setB = new Set(arrB);
	const intersection = new Set();
	for (const elem of setB) {
		if (setA.has(elem)) {
			intersection.add(elem);
		}
	}
	return [...intersection];
}
